import { ensureDefined } from '@e-socna/utils';
import { EMPTY_ARRAY } from '@e-socna/empty';

import { DocumentEditorActionType } from '../../enums/action-type.enum';
import { DocumentEditorInstantaneousContentActionType } from '../../enums/instantaneous-content-action-type.enum';
import { DocumentEditorTwoStepContentActionType } from '../../enums/two-step-content-action-type.enum';
import type { DocumentEditorState } from '../../types/editor-state.type';
import type { Editable } from '../../types/editable.type';
import type { DocumentEditorAction } from '../../types/actions.type';
import type { DocumentEditorContentAction } from '../../types/content-actions.type';

import type { ReducerContext } from '../types/reducer-context.type';

export const metadataReducer = <E extends Editable>(
  state: DocumentEditorState<E>,
  action: DocumentEditorAction<E> | DocumentEditorContentAction,
  context: Readonly<ReducerContext<E>>,
): DocumentEditorState<E> | null => {
  switch (action.type) {
    case DocumentEditorActionType.RequestTwoStepContentAction: {
      return {
        ...state,
        isDrawerOpen: true,
        currentTwoStepContentAction: {
          twoStepContentActionType: action.payload.twoStepContentActionType,
          autoBackup: action.payload.autoBackup,
          insertionPointRealPath: action.payload.insertionPointRealPath,
          textualContent: action.payload.textualContent,
          path: action.payload.path,
          alreadyUsedIds: action.payload.alreadyUsedIds,
          onlyOneChoice: action.payload.onlyOneChoice,
          orientation: action.payload.orientation,
          paperFormat: action.payload.paperFormat,
        },
      };
    }

    case DocumentEditorActionType.ClearTwoStepContentAction: {
      return {
        ...state,
        currentTwoStepContentAction: undefined,
        isDrawerOpen: false,
      };
    }

    case DocumentEditorActionType.UpdateInfo: {
      const { newEditable } = action.payload;

      return {
        ...state,
        pastVersions: EMPTY_ARRAY,
        futureVersions: EMPTY_ARRAY,
        editable: newEditable,
        currentVersion: state.currentVersion + 1,
      };
    }

    case DocumentEditorActionType.UpdateAdmin: {
      const { newAdminValues } = action.payload;

      const { newStatusId } = ensureDefined(
        {
          newStatusId: context.genericStatuses.find(
            (status) => status._id === newAdminValues.statusId,
          )?._id,
        },
        'Status not found',
      );

      return {
        ...state,
        pastVersions: EMPTY_ARRAY,
        futureVersions: EMPTY_ARRAY,
        editable: {
          ...state.editable,
          statusId: newStatusId,
        },
        currentVersion: state.currentVersion + 1,
      };
    }

    case DocumentEditorActionType.OpenDrawer:
    case DocumentEditorActionType.CloseDrawer:
    case DocumentEditorActionType.FoldAllSections:
    case DocumentEditorActionType.UnfoldAllSections:
    case DocumentEditorActionType.SetEditorMode:
    case DocumentEditorActionType.Undo:
    case DocumentEditorActionType.Redo:
    case DocumentEditorActionType.ClearHistory:
    case DocumentEditorActionType.ClearNavigationTarget:
    case DocumentEditorActionType.BumpLatestSavedVersion:
    case DocumentEditorInstantaneousContentActionType.Duplicate:
    case DocumentEditorInstantaneousContentActionType.Move:
    case DocumentEditorInstantaneousContentActionType.SetFoldingMode:
    case DocumentEditorInstantaneousContentActionType.Delete:
    case DocumentEditorInstantaneousContentActionType.Reinitialize:
    case DocumentEditorInstantaneousContentActionType.SetCustomPageBreakBefore:
    case DocumentEditorInstantaneousContentActionType.SetPageBreakBefore:
    case DocumentEditorInstantaneousContentActionType.SetCustomPageBreakAfter:
    case DocumentEditorInstantaneousContentActionType.SetPageBreakAfter:
    case DocumentEditorInstantaneousContentActionType.SetOrientation:
    case DocumentEditorInstantaneousContentActionType.SetPaperFormat:
    case DocumentEditorInstantaneousContentActionType.SetDisplayCondition:
    case DocumentEditorTwoStepContentActionType.InsertBefore:
    case DocumentEditorTwoStepContentActionType.InsertAfter:
    case DocumentEditorTwoStepContentActionType.InsertInside:
    case DocumentEditorTwoStepContentActionType.InsertStart:
    case DocumentEditorTwoStepContentActionType.InsertEnd:
    case DocumentEditorTwoStepContentActionType.Update:
    default: {
      return null; // Not handled by this reducer
    }
  }
};
